import { useTranslation } from 'react-i18next';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { I18N_KEYS } from '@/language/keys';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { MenuAction } from '@/components/Menu/Actions';
import { DownloadBtn } from '@/components/DownloadBtn/DownloadBtn';

interface DownloadMenuP {
  selected: number;
  downloadMenuAnchorEl: HTMLElement | null;
  disabled: boolean;
  isAddCopyBtn?: boolean;
  setDownloadMenuAnchorEl: (anchorEl: HTMLElement | null) => void;
  handleDownload: (index: number) => Promise<void>;
  /** Left out of the menu when missing, for a view of one file. */
  handleDownloadAll?: () => Promise<void>;
  handleCopyImage: (index: number) => Promise<void>;
}

const DownloadMenu = ({
  selected,
  downloadMenuAnchorEl,
  disabled,
  isAddCopyBtn,
  setDownloadMenuAnchorEl,
  handleDownload,
  handleDownloadAll,
  handleCopyImage,
}: DownloadMenuP) => {
  const { t } = useTranslation();

  const onDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    void handleDownload(selected);
    setDownloadMenuAnchorEl(null);
  };

  const onDownloadAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    void handleDownloadAll?.();
    setDownloadMenuAnchorEl(null);
  };

  const onCopyImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    void handleCopyImage(selected);
    setDownloadMenuAnchorEl(null);
  };

  const items: MenuAction[] = [
    {
      biEventKey: { menu: MENU_NAMES.DOWNLOAD_MENU, action: 'download' },
      name: t(I18N_KEYS.GENERAL.DOWNLOAD),
      action: onDownload,
    },
  ];

  if (handleDownloadAll) {
    items.push({
      biEventKey: { menu: MENU_NAMES.DOWNLOAD_MENU, action: 'download_all' },
      name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.DOWNLOAD_ALL),
      action: onDownloadAll,
    });
  }

  if (isAddCopyBtn) {
    items.push({
      biEventKey: { menu: MENU_NAMES.DOWNLOAD_MENU, action: 'copy_image' },
      name: t(I18N_KEYS.NODE_IMAGE_LIST.CONTEXT_MENU.COPY_IMAGE),
      action: onCopyImage,
    });
  }

  return (
    <>
      <DownloadBtn disabled={disabled} setDownloadMenuAnchorEl={setDownloadMenuAnchorEl} />
      <AppContextMenu
        width="140px"
        data-testid="node-gallery-download-menu"
        open={downloadMenuAnchorEl !== null}
        onClose={(e?: React.MouseEvent) => {
          if (e && 'stopPropagation' in e) {
            e.stopPropagation();
          }
          setDownloadMenuAnchorEl(null);
        }}
        anchorEl={downloadMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        items={items}
      />
    </>
  );
};

export default DownloadMenu;
